/**
 * Dashboard metric tile.
 *
 * A single big number with a label underneath, wrapped in an outlined box
 * with generous padding. Optional accent colour on the number (used to
 * highlight "admin users" in a warmer tone so the count stands out).
 */

import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';

export interface MetricTileProps {
  label: string;
  value: number | string;
  accent?: 'primary' | 'success' | 'warning';
  hint?: string;
}

const ACCENT_COLORS: Record<NonNullable<MetricTileProps['accent']>, string> = {
  primary: '#0972D3',
  success: '#00A887',
  warning: '#D2650D',
};

export const MetricTile: React.FC<MetricTileProps> = ({ label, value, accent, hint }) => {
  const valueColor = accent ? ACCENT_COLORS[accent] : '#0F1111';
  return (
    <Box type="outline" spacingInset="500" minWidth={180}>
      <Column spacing="xsmall" alignmentHorizontal="start">
        <span
          style={{
            fontSize: '32px',
            fontWeight: 700,
            lineHeight: 1,
            color: valueColor,
            letterSpacing: '-0.5px',
          }}
        >
          {value}
        </span>
        <Text type="b300" color="primary">
          {label}
        </Text>
        {hint && (
          <Text type="b200" color="secondary">
            {hint}
          </Text>
        )}
      </Column>
    </Box>
  );
};
